/**
 * Homepage widget: "ดาต้าไปป์ไลน์ & รีทาร์เก็ตติ้ง" — the store-wide data
 * pipeline & retargeting readiness (tracking health, retargetable audiences,
 * campaign segments, recoverable carts). Reuses the exact PipelineSection from
 * the /analytics/social page, wired to the same get_social_stats source.
 * Renders nothing until the pipeline block is warm, so the homepage never
 * shows an empty box.
 */

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useSocialStats } from '@/hooks/useLiveVisitors'
import { PipelineSection } from '../SocialExtraCards'

const shortDay = (d: string) =>
  `${parseInt(d.slice(8, 10), 10)}/${parseInt(d.slice(5, 7), 10)}`

export function DashboardPipelineWidget() {
  const { data } = useSocialStats()
  const pages = data?.pages ?? []

  // Chat-per-day across ALL pages (FB + IG) — the homepage has no channel
  // selector, so this is always the store-wide aggregate.
  const chatSeries = useMemo(() => {
    const byDate = new Map<string, number>()
    for (const pg of pages) {
      for (const pt of pg.chat_daily ?? []) {
        byDate.set(pt.d, (byDate.get(pt.d) ?? 0) + pt.v)
      }
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({ day: shortDay(date), value }))
  }, [pages])

  if (!data?.pipeline) return null

  return (
    <section className="space-y-4">
      <div>
        <Link
          to="/analytics/social"
          className="group inline-flex items-center gap-1.5"
          title="เปิดแดชบอร์ดโซเชียลมีเดีย"
        >
          <h2 className="text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
            ดาต้าไปป์ไลน์ &amp; รีทาร์เก็ตติ้ง
          </h2>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
        </Link>
        <p className="text-[11px] text-muted-foreground">
          ฐานลูกค้าทั้งร้าน (รวมทุกช่องทาง ไม่เฉพาะโซเชียล) — สินทรัพย์ที่ใช้ยิงโฆษณา
          รีทาร์เก็ต และกู้ตะกร้า
        </p>
      </div>
      <PipelineSection
        pipeline={data.pipeline}
        chatUsers={data.chat_users}
        chatSeries={chatSeries}
      />
    </section>
  )
}
